import { Head, Link, useForm } from '@inertiajs/react'
import { useState } from 'react'
import AlertMessages from './_components/AlertMessages'
import ImportSettings from './_components/ImportSettings'
import FileUpload from './_components/FileUpload'

interface Organization {
    id: number
    name: string
    slug: string
}

interface Props {
    organizations: Organization[]
    errors?: string[]
    message?: string
}

export default function New({organizations, errors, message}: Props) {
    const [selectedFile, setSelectedFile] = useState<File | null>(null)

    const {data, setData, post, processing} = useForm({
        organization_upload: {
            file: null as File | null,
            import_mode: 'create_new',
            organization_id: ''
        }
    })

    const handleFileSelect = (file: File | null) => {
        setSelectedFile(file)
        setData('organization_upload.file', file)
    }

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()

        if (!data.organization_upload.file) {
            alert('Please select a file to upload')
            return
        }

        // Validate organization selection for append/replace modes
        if (data.organization_upload.import_mode !== 'create_new' && !data.organization_upload.organization_id) {
            alert('Please select an organization for append or replace mode')
            return
        }

        post('/admin/organization_uploads')
    }

    return (
        <>
            <Head title="New Organization Upload - Super Admin"/>

            <div className="min-h-screen bg-gray-50">
                {/* Header */}
                <div className="bg-white shadow">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex justify-between items-center py-6">
                            <div className="flex items-center space-x-4">
                                <Link href="/admin/organization_uploads"
                                      className="text-indigo-600 hover:text-indigo-700">
                                    ← Back to Uploads
                                </Link>
                                <h1 className="text-2xl font-bold text-gray-900">
                                    New Organization Upload
                                </h1>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main Content */}
                <div className="max-w-3xl mx-auto py-6 sm:px-6 lg:px-8">
                    <div className="px-4 py-6 sm:px-0">
                        <AlertMessages message={message} errors={errors} />

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <ImportSettings
                                importMode={data.organization_upload.import_mode}
                                organizationId={data.organization_upload.organization_id}
                                organizations={organizations}
                                onImportModeChange={(mode) => setData('organization_upload.import_mode', mode)}
                                onOrganizationIdChange={(id) => setData('organization_upload.organization_id', id)}
                            />

                            <FileUpload
                                selectedFile={selectedFile}
                                onFileSelect={handleFileSelect}
                            />

                            {/* Submit */}
                            <div className="flex justify-end space-x-3">
                                <Link
                                    href="/admin/organization_uploads"
                                    className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                >
                                    Cancel
                                </Link>
                                <button
                                    type="submit"
                                    disabled={processing || !data.organization_upload.file}
                                    className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {processing ? 'Uploading...' : 'Start Import'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </>
    )
}